import { useEffect, useId, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { ImageWithUpload } from '@hero/schema';
import { fileToBase64 } from '@hero/web-utils';
import { FileUploader } from '../../File/FileUploader';
import { FieldShell } from './FieldShell';
import type { RhfFieldBaseProps } from './types';

const DEFAULT_ACCEPTED_DOCUMENT_TYPES: Record<string, string[]> = {
  'application/pdf': [],
  'image/jpeg': [],
  'image/png': [],
};

const DEFAULT_ACCEPTED_IMAGE_TYPES: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
};

export interface RhfImageFieldProps extends RhfFieldBaseProps {
  /**
   * Override the default upload-button label rendered inside the
   * empty-state of `FileUploader`. When omitted we fall back to
   * the field's `placeholder`.
   */
  uploadLabel?: string;
  /**
   * Override which file types the dropzone accepts. Sensible
   * defaults vary by sub-component (legal documents: pdf + image;
   * logo: image-only).
   */
  acceptedFileTypes?: Record<string, string[]>;
  /**
   * Restrict to image-only when the field is a logo / profile
   * picture. Has no effect when `acceptedFileTypes` is provided.
   */
  imageOnly?: boolean;
}

/**
 * `RhfImageField` — RHF-aware single-image field bound to an
 * `ImageWithUpload` slot (the shape used by every image field on
 * `OrganizationProfile`).
 *
 * It owns local `File[]` state for the `FileUploader` so the
 * thumbnail renders immediately on pick; the `RHF` value stays
 * `ImageWithUpload | undefined`. On submit, the form sends the
 * `ImageWithUpload` value as-is — persisted images
 * (`{ id, url }`) round-trip unchanged, fresh picks
 * (`{ upload: base64 }`) get materialised server-side via
 * `ImageService.upload(...)`.
 *
 * Hydration: when the form's external value changes (e.g. after a
 * successful update flips the cache from `{ upload }` →
 * `{ id, url }`), we resync the displayed thumbnail by clearing
 * the local File array. We deliberately don't try to re-create a
 * `File` from a remote URL — `existingUrls` covers the preview.
 */
export function RhfImageField({
  name,
  label,
  helper,
  required,
  financing,
  disabled,
  className,
  placeholder,
  uploadLabel,
  acceptedFileTypes,
  imageOnly,
}: RhfImageFieldProps & { label: string }) {
  const { control } = useFormContext();
  const generatedId = useId();
  const inputId = `field-${name}-${generatedId}`;

  const acceptTypes = useMemo(
    () =>
      acceptedFileTypes ??
      (imageOnly ? DEFAULT_ACCEPTED_IMAGE_TYPES : DEFAULT_ACCEPTED_DOCUMENT_TYPES),
    [acceptedFileTypes, imageOnly],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = field.value as ImageWithUpload | undefined;
        // "Filled" once we have an existing remote image OR a
        // freshly-staged base64 upload. The local `File[]` state
        // inside `ImageFieldInner` is also accepted as filled
        // (the controller mirrors that into `value` on pick).
        const satisfied = Boolean(value?.url) || Boolean(value?.upload);
        return (
          <ImageFieldInner
            label={label}
            helper={helper}
            required={required}
            financing={financing}
            financingSatisfied={satisfied}
            disabled={disabled}
            className={className}
            placeholder={placeholder}
            uploadLabel={uploadLabel}
            acceptTypes={acceptTypes}
            error={fieldState.error?.message}
            htmlFor={inputId}
            value={value}
            onChange={field.onChange}
          />
        );
      }}
    />
  );
}

interface InnerProps {
  label: string;
  helper?: string;
  required?: boolean;
  financing?: RhfImageFieldProps['financing'];
  financingSatisfied?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  uploadLabel?: string;
  acceptTypes: Record<string, string[]>;
  error?: string;
  htmlFor: string;
  value: ImageWithUpload | undefined;
  onChange: (value: ImageWithUpload | undefined) => void;
}

function ImageFieldInner({
  label,
  helper,
  required,
  financing,
  financingSatisfied,
  disabled,
  className,
  placeholder,
  uploadLabel,
  acceptTypes,
  error,
  htmlFor,
  value,
  onChange,
}: InnerProps) {
  const [files, setFiles] = useState<File[]>([]);

  // When the form's external value changes (e.g. cache flips from
  // `{ upload }` to `{ id, url }` post-save), drop the local File[]
  // so we don't keep the now-stale thumbnail next to the real URL.
  // We treat any change of the *identity* of the value (by URL or
  // by upload data-uri) as a reset signal.
  const remoteUrl = value?.url;
  const remoteUploadKey = isString(value?.upload) ? value?.upload : undefined;
  useEffect(() => {
    if (remoteUrl) setFiles([]);
  }, [remoteUrl]);

  const existingUrls = useMemo(() => {
    if (typeof remoteUrl === 'string' && remoteUrl) return [remoteUrl];
    if (typeof remoteUploadKey === 'string' && remoteUploadKey) return [remoteUploadKey];
    return [];
  }, [remoteUrl, remoteUploadKey]);

  const handleFiles = async (next: File[]) => {
    setFiles(next);
    const first = next[0];
    if (!first) {
      onChange(undefined);
      return;
    }
    const upload = await fileToBase64(first);
    onChange({ upload } as ImageWithUpload);
  };

  return (
    <FieldShell
      label={label}
      helper={helper}
      required={required}
      financing={financing}
      financingSatisfied={financingSatisfied}
      error={error}
      htmlFor={htmlFor}
      className={className}>
      <FileUploader
        files={files}
        onChange={handleFiles}
        acceptedFileTypes={acceptTypes}
        label={uploadLabel ?? placeholder ?? label}
        existingUrls={existingUrls}
      />
      {disabled ? <span className="sr-only">{label}</span> : null}
    </FieldShell>
  );
}

function isString(v: unknown): v is string {
  return typeof v === 'string';
}
